/**
 * @fileoverview Assist utility functions for deriving field status from import + current data.
 */
import type { ImportedFieldPresence,ImportPayload } from '@/services/types/case-ingestion-assist-types';

import type { AssistFieldEntry, AssistFieldRow, AssistFieldStatus } from './types';

/**
 * Safely access a nested value by dot-path.
 */
function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Determine the assist status for a single field.
 *
 * Logic:
 * - If presence is `not_applicable`, status is always `not_applicable`.
 * - If presence is `missing`, status is `no_import_value`.
 * - If presence is `present` and current value is populated, status is `resolved`.
 * - If presence is `present` and current value is empty/null, status is `needs_review`.
 */
function deriveFieldStatus(
  presence: ImportedFieldPresence,
  currentValue: unknown,
  isMultiEntity: boolean,
): AssistFieldStatus {
  if (presence === 'not_applicable') return 'not_applicable';
  if (presence === 'missing') return 'no_import_value';

  // presence === 'present'
  if (hasValue(currentValue, isMultiEntity)) return 'resolved';
  return 'needs_review';
}

/**
 * Check if a current value counts as "populated" for gap resolution.
 */
function hasValue(value: unknown, isMultiEntity: boolean): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (isMultiEntity && Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;
  return Boolean(value);
}

/**
 * Build an AssistFieldRow from a registry entry, import payload, and current value.
 */
export function buildFieldRow(
  entry: AssistFieldEntry,
  payload: ImportPayload,
  currentValue: unknown,
): AssistFieldRow {
  const importedValue = getByPath(payload, entry.importedValuePath);
  const rawPresence = getByPath(payload, entry.importedPresencePath);
  const importedPresence: ImportedFieldPresence =
    rawPresence === 'present' || rawPresence === 'missing' || rawPresence === 'not_applicable'
      ? rawPresence
      : 'missing';

  return {
    fieldId: entry.fieldId,
    label: entry.label,
    stepId: entry.stepId,
    severity: entry.severity,
    status: deriveFieldStatus(importedPresence, currentValue, !!entry.isMultiEntity),
    importedValue,
    importedPresence,
    currentValue,
    isMultiEntity: !!entry.isMultiEntity,
  };
}

/**
 * Check if a field row represents an unresolved gap.
 * `not_applicable` fields never count as unresolved.
 * `no_import_value` always counts as unresolved (data missing from CMS).
 * `needs_review` is always unresolved.
 */
export function isUnresolved(row: AssistFieldRow): boolean {
  return row.status === 'needs_review' || row.status === 'no_import_value';
}
